<template>
  <div>
    <v-menu
      v-model="showNotifications"
      class="notifications-menu-container"
      :close-on-content-click="true"
      offset-y
      allow-overflow
      bottom
      left
      max-width="375"
      max-height="450"
      content-class="notifications-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          class="mr-6 notifications-btn"
          v-on="on"
          v-bind="attrs"
          :ripple="false"
          data-testid="notifications-bell-icon"
        >
          <v-badge
            color="secondary"
            :value="notificationCntText"
            :content="notificationCntText"
            overlap
          >
            <v-icon>$bell</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <div class="justify-space-between d-flex top-bar noti-bar">
        <div class="notifications-title" data-testid="notifications-title">
          {{ $t('notifications.notifications') }}
        </div>
        <v-btn
          text
          @click="markAllAsRead"
          data-testid="notification-mark-all-as-read"
          color="blue"
          :disabled="notificationCount === 0"
          class="font-weight-bold"
          >{{ $t('notifications.markAllAsRead') }}</v-btn
        >
      </div>
      <v-card class="notifications-list" ref="notificationsList" scrollable>
        <v-list class="py-0">
          <template v-if="notificationListTop15.length > 0">
            <template v-for="notification in notificationListTop15">
              <SingleNotification
                :key="notification.notificationLogId"
                :notification="notification"
                @markItemAsRead="markItemAsRead"
                :display24HourTime="display24HourTime"
              />
            </template>
            <v-list-item
              @click="goToNotifications"
              v-if="notificationListTop15.length == 15"
              key="lastNotification"
              class="pb-5"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('notifications.viewMore') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="notificationListTop15.length == 0">
            <v-list-item
              style="min-height: 80px"
              class="notification-item notification-item-no-hover"
              ><v-list-item-content
                ><v-list-item-title
                  data-testid="notification-no-display"
                  style="text-align: middle"
                  >{{ $t('notifications.noNotifications') }}</v-list-item-title
                ></v-list-item-content
              ></v-list-item
            >
          </template>
        </v-list>
      </v-card>
      <div class="d-flex justify-space-between py-0 bottom-bar noti-bar">
        <v-btn
          data-testid="notification-view-all"
          depressed
          text
          color="blue"
          @click="goToNotifications"
          class="font-weight-bold"
          >{{ $t('notifications.viewAll') }}</v-btn
        >
        <v-btn
          class="font-weight-bold"
          depressed
          text
          color="blue"
          @click="goToSettings"
          >{{ $t('notifications.notificationSettings') }}</v-btn
        >
      </div>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { getNewNotifications, getAllNotifications } from '@/utils/webSockets'
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import SingleNotification from './SingleNotification.vue'
import {
  CASE_COMMENT_ADDED,
  CAMERA_MOTION_DETECTED,
  DEVICE_STATUS_UPDATED,
  DIGITAL_INPUT,
  VCA_EVENT,
} from '@/constants/notificationTypes.js'

export default defineComponent({
  name: 'NotificationsIcon',
  data() {
    return {
      showNotifications: false,
      notificationsPoll: null,
      currentTime: '',
      hasShownPreviewNotifications: false,
    }
  },
  components: {
    SingleNotification,
  },
  methods: {
    ...mapActions('Notifications', [
      'callSetReadAllNotifications',
      'callSetReadNotification',
      'showNotificationsSettings',
      'showAllNotifications',
    ]),
    toggleNotificationsList() {
      this.showNotifications = !this.showNotifications
    },
    markItemAsRead(item) {
      this.callSetReadNotification(item.notificationLogId)
      const eventType = item.eventTypeId
      switch (eventType) {
        case CASE_COMMENT_ADDED:
          this.$router.push({
            name: 'Case',
            params: { id: item.eventObjectId },
          })
          break
        case CAMERA_MOTION_DETECTED:
        case DEVICE_STATUS_UPDATED:
        case DIGITAL_INPUT:
          this.$router.push({
            name: 'SingleCamera',
            params: { id: item.eventObjectId },
          })
          break
        case VCA_EVENT:
          this.$router.push({
            name: 'SingleCamera',
            params: { id: item.eventObjectId },
            query: { eventId: JSON.parse(item.notificationPayload).Id },
          })
          break
        default:
          break
      }
    },
    async markAllAsRead() {
      await this.callSetReadAllNotifications({ MarkAllAsRead: true })
      this.showNotifications = false
      if (this.$route.name === 'NotificationsList') {
        this.showAllNotifications({ viewAll: true })
      }
    },
    goToNotifications() {
      this.showNotifications = false
      if (this.$route.name != 'NotificationsList') {
        this.$router.push({
          name: 'NotificationsList',
        })
      } else {
        this.showAllNotifications({ viewAll: true })
      }
    },
    goToSettings() {
      this.showNotifications = false
      if (this.$route.name != 'Profile') {
        this.$router.push({
          name: 'Profile',
          params: {
            notificationsSettingsGeneral: true,
          },
        })
      } else {
        this.showNotificationsSettings({ general: true })
      }
    },
    loopGetNewNotifications() {
      this.notificationsPoll = setInterval(() => {
        let payload = {
          CreatedOn: this.currentTime,
          RequestUserId: this.user.userId,
        }
        getNewNotifications(payload)
        this.currentTime = DateTime.utc()
      }, 30000)
    },
    getPreviewNotifications() {
      if (this.connected && !this.hasShownPreviewNotifications) {
        this.currentTime = DateTime.utc()
        getAllNotifications({
          RequestUserId: this.user.userId,
          MaxReturnCount: 15,
        })
        this.loopGetNewNotifications()
        this.hasShownPreviewNotifications = true
      }
    },
  },
  watch: {
    connected() {
      this.getPreviewNotifications()
    },
    isLoggedIn() {
      if (!this.isLoggedIn) {
        clearInterval(this.notificationsPoll)
      }
    },
    showNotifications() {
      this.$nextTick(() => {
        this.$vuetify.goTo('.notifications-list', {
          container: '.notifications-menu',
          duration: 0,
        })
      })
    },
  },
  created() {
    this.getPreviewNotifications()
  },
  beforeDestroy() {
    clearInterval(this.notificationsPoll)
    this.hasShownPreviewNotifications = false
  },
  computed: {
    ...mapState('WebSocket', ['connected']),
    ...mapState('Security', ['isLoggedIn', 'user']),
    ...mapGetters('Security', ['display24HourTime']),
    ...mapGetters('Notifications', [
      'notificationCount',
      'notificationListTop15',
    ]),
    notificationCntText() {
      return this.notificationCount > 999 ? '999+' : this.notificationCount
    },
  },
})
</script>

<style lang="scss">
.notifications-btn {
  &.v-btn:focus::before {
    opacity: 0 !important;
  }
}
.notifications-menu {
  .noti-bar {
    position: sticky;
    background-color: $color-darkBackground;
    z-index: 99;
    overflow: hidden;
    .v-btn:hover::before {
      opacity: 0;
    }
  }
  .top-bar {
    background-color: $color-containerGray;
    top: 0px;
    .notifications-title {
      font-weight: 800;
      height: 36px;
      padding: 6px 16px 0 16px;
    }
  }
  .bottom-bar {
    background-color: $color-containerGray;
    bottom: 0px;
    box-shadow: 0px 3px 3px 3px rgba(0, 0, 0, 0.58);
  }
  .notifications-list {
    overflow: hidden;
    z-index: -1;
    .notification-item {
      background-color: $color-gray;
      &.unread-notification {
        background-color: $color-medGray;
      }
      &:hover {
        cursor: pointer;
        background-color: $color-hoverGray;
      }
      .v-list-item__title {
        text-overflow: none;
        white-space: break-spaces;
      }
    }
    .notification-item-no-hover {
      &:hover {
        cursor: auto;
        background-color: $color-gray;
      }
    }
  }
}
</style>
